/**
 * Semantic Search for Predictive Search
 *
 * Adds TF-IDF based semantic search with German NLP to supplement
 * Shopify's native predictive search. Uses /products.json storefront API.
 */
(function () {
  'use strict';

  // ── Configuration ──────────────────────────────────────────────────
  var CACHE_KEY = 'semantic-search-products-v1';
  var INDEX_CACHE_KEY = 'semantic-search-index-v1';
  var MAX_RESULTS = 5;
  var PRODUCTS_PER_PAGE = 250;

  // Field weights for TF-IDF scoring
  var FIELD_WEIGHTS = {
    title: 3.0,
    tags: 2.5,
    product_type: 2.0,
    vendor: 1.5,
    description: 1.0
  };

  // ── German Stop Words ──────────────────────────────────────────────
  var STOP_WORDS = new Set([
    'der', 'die', 'das', 'den', 'dem', 'des', 'ein', 'eine', 'einer', 'einem', 'einen', 'eines',
    'und', 'oder', 'aber', 'als', 'auch', 'auf', 'aus', 'bei', 'bis', 'fur', 'fuer',
    'gegen', 'in', 'mit', 'nach', 'nicht', 'noch', 'nur', 'uber', 'ueber', 'um',
    'von', 'vor', 'zu', 'zum', 'zur', 'wie', 'was', 'wer', 'wo', 'wenn',
    'ist', 'sind', 'hat', 'haben', 'wird', 'werden', 'kann', 'konnen', 'soll', 'sollen',
    'sich', 'ich', 'du', 'er', 'sie', 'es', 'wir', 'ihr',
    'an', 'am', 'im', 'vom', 'ins',
    'so', 'da', 'sehr', 'mehr', 'schon', 'mal', 'doch', 'hier', 'dort',
    'alle', 'alles', 'jede', 'jeder', 'jedes', 'kein', 'keine',
    'this', 'that', 'the', 'and', 'for', 'with', 'from', 'not'
  ]);

  // ── Synonym Map (organized by product domain) ─────────────────────
  var SYNONYMS = {
    // ── Desinfektion & Hygiene ──────────────────────────────────────
    'desinfekt': ['hygien', 'keimfrei', 'antibakteri', 'steril', 'reinig', 'sauber', 'keimtoet', 'bakterienabtot', 'desinfizie'],
    'hygien': ['desinfekt', 'sauber', 'reinig', 'steril', 'keimfrei'],
    'keimfrei': ['desinfekt', 'steril', 'hygien', 'antibakteri', 'asept'],
    'keimtoet': ['desinfekt', 'antibakteri', 'steril', 'abtot'],
    'keim': ['desinfekt', 'steril', 'hygien', 'antibakteri', 'bakteri', 'abtot'],
    'bakterienabtot': ['desinfekt', 'keimtoet', 'antibakteri'],
    'antibakteri': ['desinfekt', 'keimfrei', 'keimtoet', 'hygien'],
    'haend': ['handdesinfekt', 'handhygien', 'handreinig', 'handpfleg'],
    'handdesinfekt': ['haend', 'handhygien', 'handreinig', 'desinfekt'],
    'handhygien': ['haend', 'handdesinfekt', 'handreinig'],
    'handreinig': ['haend', 'handdesinfekt', 'handhygien', 'seif', 'wasch'],
    'spender': ['dosier', 'dispens', 'wand', 'aufstell', 'seifen', 'desinfektionsmittel'],
    'dosier': ['spender', 'dispens'],
    'dispens': ['spender', 'dosier'],
    'steckbeck': ['bettschuessel', 'bettpfann', 'pfleg', 'patient', 'steckbeckenspu'],
    'bettschuessel': ['steckbeck', 'bettpfann', 'patient'],
    'bettpfann': ['steckbeck', 'bettschuessel', 'patient'],
    'steckbeckenspu': ['steckbeck', 'reinig', 'desinfekt', 'thermisch'],
    'endoskop': ['flexibel', 'schlauch', 'optik', 'kamerasystem', 'aufbereit', 'endoskopaufbereit'],
    'endoskopaufbereit': ['endoskop', 'aufbereit', 'reinig', 'desinfekt'],
    'opschuh': ['reinig', 'schuh', 'hygien', 'desinfekt'],

    // ── Instrumentenaufbereitung ────────────────────────────────────
    'rdg': ['reinig', 'desinfekt', 'automat', 'maschin', 'aufbereit', 'reinigungs', 'desinfektionsgeraet'],
    'aufbereit': ['reinig', 'desinfekt', 'steril', 'rdg', 'wiederverwendbar', 'recycl'],
    'dental': ['zahn', 'zahnarzt', 'praxis', 'mundchirurg', 'zahnmedizin'],
    'zahn': ['dental', 'zahnarzt', 'mundchirurg', 'zahnmedizin'],
    'zahnarzt': ['dental', 'zahn', 'praxis', 'zahnmedizin'],
    'labor': ['forsch', 'wissens', 'analys', 'pruef', 'laborgeraet'],
    'forsch': ['labor', 'wissens', 'analys'],
    'medical': ['medizin', 'klinik', 'krankenhaus', 'arzt', 'chirurg'],
    'medizin': ['medical', 'klinik', 'krankenhaus', 'arzt', 'chirurg'],
    'klinik': ['medical', 'medizin', 'krankenhaus', 'arzt'],
    'krankenhaus': ['medical', 'medizin', 'klinik', 'arzt', 'hospital'],
    'arzt': ['medical', 'medizin', 'klinik', 'praxis'],
    'chirurg': ['medical', 'medizin', 'steril', 'instrument', 'einweg'],
    'maschin': ['rdg', 'automat', 'geraet'],
    'automat': ['rdg', 'maschin', 'geraet'],
    'geschirrspuel': ['spueltechnik', 'thermisch', 'desinfekt', 'reinig', 'spuelmasch'],

    // ── Sterilisation ──────────────────────────────────────────────
    'steril': ['keimfrei', 'desinfekt', 'asept', 'autokla', 'abtot', 'sterilisation'],
    'sterilisation': ['steril', 'autokla', 'dampf', 'keimfrei'],
    'dampf': ['wasserdampf', 'feucht', 'autokla', 'druck', 'dampfsteril'],
    'dampfsteril': ['dampf', 'autokla', 'steril', 'sterilisation'],
    'autokla': ['dampf', 'steril', 'druck', 'dampfsteril'],
    'niedertemperatur': ['kalt', 'plasma', 'ethylenoxid', 'formaldehyd', 'steril'],
    'plasma': ['niedertemperatur', 'steril', 'kalt'],
    'ethylenoxid': ['niedertemperatur', 'steril', 'formaldehyd'],
    'formaldehyd': ['niedertemperatur', 'steril', 'ethylenoxid'],
    'indikator': ['test', 'nachweis', 'kontroll', 'pruef', 'ueberwach', 'biolog', 'chemisch'],
    'nachweis': ['indikator', 'test', 'kontroll', 'pruef'],
    'kontroll': ['indikator', 'test', 'nachweis', 'pruef', 'ueberwach'],
    'pruef': ['indikator', 'test', 'nachweis', 'kontroll', 'ueberwach'],
    'ueberwach': ['indikator', 'kontroll', 'pruef', 'test'],
    'biolog': ['indikator', 'test', 'chemisch'],
    'chemisch': ['indikator', 'test', 'biolog'],
    'asept': ['steril', 'keimfrei', 'hygien'],
    'abtot': ['steril', 'desinfekt', 'keimtoet', 'keim'],

    // ── Verpackung & Lagerung ──────────────────────────────────────
    'verpack': ['einschlag', 'huell', 'schutz', 'barrier', 'beutel', 'tyvek', 'sterilverpack', 'sterilisierverpack'],
    'sterilverpack': ['verpack', 'vlies', 'beutel', 'tyvek', 'sterilisierverpack'],
    'sterilisierverpack': ['verpack', 'vlies', 'beutel', 'tyvek', 'sterilverpack'],
    'vlies': ['geweb', 'textil', 'einschlag', 'sterilisierverpack', 'verpack'],
    'geweb': ['vlies', 'textil', 'stoff'],
    'textil': ['vlies', 'geweb', 'stoff'],
    'einschlag': ['verpack', 'vlies', 'huell'],
    'beutel': ['verpack', 'tyvek', 'sterilverpack', 'tuet'],
    'tyvek': ['verpack', 'beutel', 'sterilverpack', 'barrier'],
    'tape': ['kleb', 'verschluss', 'abdicht', 'markier', 'sterilisierband'],
    'kleb': ['tape', 'verschluss', 'abdicht', 'sterilisierband'],
    'sterilisierband': ['tape', 'kleb', 'indikator', 'markier'],
    'lager': ['aufbewahr', 'schrank', 'trocken', 'regal', 'sterilgutlager'],
    'aufbewahr': ['lager', 'schrank', 'trocken', 'sterilgutlager'],
    'sterilgutlager': ['lager', 'aufbewahr', 'schrank', 'trocken'],
    'schrank': ['lager', 'aufbewahr', 'trocken', 'trockenschrank'],
    'trockenschrank': ['schrank', 'lager', 'trocken', 'aufbewahr'],
    'barrier': ['verpack', 'schutz', 'huell', 'tyvek'],
    'huell': ['verpack', 'einschlag', 'barrier', 'schutz'],

    // ── Gastronomie ────────────────────────────────────────────────
    'kochtechnik': ['herd', 'ofen', 'grill', 'braeter', 'fritteus', 'dampfgar', 'kombidaempf'],
    'herd': ['kochtechnik', 'ofen', 'koch'],
    'ofen': ['kochtechnik', 'herd', 'back', 'grill'],
    'grill': ['kochtechnik', 'ofen', 'braeter'],
    'braeter': ['kochtechnik', 'grill', 'brat'],
    'fritteus': ['kochtechnik', 'fett', 'back'],
    'dampfgar': ['kochtechnik', 'kombidaempf', 'dampf', 'gar'],
    'kombidaempf': ['kochtechnik', 'dampfgar', 'ofen'],
    'kaeltetechnik': ['kuehl', 'tiefkuehl', 'gefrier', 'kuehlung', 'lager', 'kalt'],
    'kuehl': ['kaeltetechnik', 'tiefkuehl', 'gefrier', 'kuehlung', 'kuehlschrank'],
    'kuehlschrank': ['kuehl', 'kaeltetechnik', 'kuehlung', 'lager'],
    'tiefkuehl': ['kaeltetechnik', 'kuehl', 'gefrier', 'frost'],
    'gefrier': ['kaeltetechnik', 'kuehl', 'tiefkuehl', 'frost'],
    'kuehlung': ['kaeltetechnik', 'kuehl', 'tiefkuehl'],
    'spueltechnik': ['geschirrspuel', 'spuelmasch', 'reinig', 'glaes'],
    'spuelmasch': ['spueltechnik', 'geschirrspuel', 'reinig'],
    'glaes': ['spueltechnik', 'spuelmasch', 'glasspuel'],
    'vorbereitung': ['schneid', 'misch', 'ruehr', 'knet', 'kuechenmasch'],
    'schneid': ['vorbereitung', 'misch', 'kuechenmasch'],
    'misch': ['vorbereitung', 'ruehr', 'knet'],
    'ruehr': ['vorbereitung', 'misch', 'knet'],
    'knet': ['vorbereitung', 'misch', 'ruehr'],
    'kuechenmasch': ['vorbereitung', 'schneid', 'misch', 'ruehr', 'knet'],
    'ausgab': ['bar', 'thek', 'bueffet'],
    'bar': ['ausgab', 'thek', 'getraenk'],
    'getraenk': ['bar', 'kuehl', 'ausgab'],

    // ── Cross-domain natural-language concepts ─────────────────────
    'schutz': ['sicherheit', 'barrier', 'verpack', 'hygien', 'schuetzend'],
    'sicherheit': ['schutz', 'schuetzend', 'barrier'],
    'einweg': ['wegwerf', 'disposable', 'einmal'],
    'wegwerf': ['einweg', 'disposable', 'einmal'],
    'patient': ['pfleg', 'steckbeck', 'hygien', 'bett', 'versorg'],
    'pfleg': ['patient', 'versorg', 'behandl', 'hygien'],
    'reinig': ['sauber', 'hygien', 'desinfekt', 'putz', 'spuel', 'wasch'],
    'sauber': ['reinig', 'hygien', 'desinfekt', 'putz'],
    'spuel': ['reinig', 'wasch', 'geschirrspuel', 'spueltechnik'],
    'wasch': ['reinig', 'sauber', 'seif', 'spuel'],
    'thermisch': ['desinfekt', 'dampf', 'steckbeckenspu', 'geschirrspuel'],
    'wiederverwendbar': ['aufbereit', 'mehrweg', 'recycl'],
    'mehrweg': ['wiederverwendbar', 'aufbereit'],
    'instrument': ['werkzeug', 'scher', 'klemm', 'pinzett', 'aufbereit', 'steril'],

    // ── Existing specific product terms ────────────────────────────
    'handschuh': ['einweg', 'nitril', 'latex', 'vinyl', 'schutzhandschuh'],
    'nitril': ['handschuh', 'einweg'],
    'latex': ['handschuh', 'einweg'],
    'pflaster': ['wundversorg', 'verband', 'wund'],
    'wundversorg': ['pflaster', 'verband', 'wund'],
    'verband': ['pflaster', 'wundversorg', 'wund', 'binde'],
    'atemschutz': ['mask', 'ffp', 'mundschutz'],
    'mask': ['atemschutz', 'mundschutz', 'ffp'],
    'mundschutz': ['mask', 'atemschutz'],
    'seif': ['wasch', 'hand', 'reinig', 'handreinig'],
    'diagnos': ['untersuch', 'test', 'mess'],
    'untersuch': ['diagnos', 'test'],
    'notfall': ['erst', 'rettung', 'notfallversorg'],
    'inkontinenz': ['pfleg', 'einlage', 'windel'],
    'katheter': ['urin', 'drainage', 'sonde'],
    'tupfer': ['kompresse', 'gaze', 'wundauflage'],
    'kompresse': ['tupfer', 'gaze', 'wundauflage'],
    'einwegventil': ['endoskop', 'ventil', 'einweg'],
    'ventil': ['einwegventil', 'endoskop']
  };

  // ── German Suffixes for Stemming ───────────────────────────────────
  var SUFFIXES = [
    'ierung', 'ungen', 'keit', 'heit', 'lich', 'isch', 'tion', 'ieren',
    'ung', 'bar', 'sam', 'eur', 'ent', 'ant', 'ell',
    'en', 'er', 'es', 'em', 'et', 'te', 'st'
  ];

  // ── Umlaut Map ─────────────────────────────────────────────────────
  var UMLAUT_MAP = {
    '\u00e4': 'ae', '\u00f6': 'oe', '\u00fc': 'ue', '\u00df': 'ss',
    '\u00c4': 'ae', '\u00d6': 'oe', '\u00dc': 'ue'
  };

  // ── State ──────────────────────────────────────────────────────────
  var products = null;
  var invertedIndex = null;
  var idfMap = null;
  var docCount = 0;
  var vocabulary = null;
  var trigramIndex = null; // maps trigram → Set of vocabulary terms
  var isReady = false;
  var isLoading = false;

  // ── Tokenizer ──────────────────────────────────────────────────────

  function normalizeUmlauts(str) {
    return str.replace(/[\u00e4\u00f6\u00fc\u00df\u00c4\u00d6\u00dc]/g, function (ch) {
      return UMLAUT_MAP[ch] || ch;
    });
  }

  function stripHtml(html) {
    if (!html) return '';
    return html.replace(/<[^>]*>/g, ' ').replace(/&[^;]+;/g, ' ');
  }

  function stem(word) {
    if (word.length < 4) return word;
    for (var i = 0; i < SUFFIXES.length; i++) {
      var suffix = SUFFIXES[i];
      if (word.length > suffix.length + 2 && word.endsWith(suffix)) {
        return word.slice(0, -suffix.length);
      }
    }
    return word;
  }

  function tokenize(text) {
    if (!text) return [];
    var normalized = normalizeUmlauts(text.toLowerCase());
    var words = normalized.match(/[a-z0-9]{2,}/g);
    if (!words) return [];

    var tokens = [];
    for (var i = 0; i < words.length; i++) {
      var w = words[i];
      if (STOP_WORDS.has(w)) continue;
      tokens.push(stem(w));
    }
    return tokens;
  }

  function decompoundWord(word, vocab) {
    if (word.length < 8) return [word];
    var parts = [];
    // Try splitting at every position from 4..len-4
    for (var i = 4; i <= word.length - 4; i++) {
      var left = word.slice(0, i);
      var right = word.slice(i);
      // Also try removing linking 's' or 'n'
      var rightNoLink = null;
      if (right.charAt(0) === 's' || right.charAt(0) === 'n') {
        rightNoLink = right.slice(1);
      }
      if (vocab.has(left)) {
        if (vocab.has(right)) {
          parts.push(left, right);
          return parts;
        }
        if (rightNoLink && rightNoLink.length >= 3 && vocab.has(rightNoLink)) {
          parts.push(left, rightNoLink);
          return parts;
        }
      }
    }
    return [word];
  }

  // ── Product Fetcher ────────────────────────────────────────────────

  function fetchProducts() {
    if (isLoading) return;
    isLoading = true;

    // Try sessionStorage cache first
    try {
      var cached = sessionStorage.getItem(CACHE_KEY);
      if (cached) {
        products = JSON.parse(cached);
        buildIndex();
        isLoading = false;
        return;
      }
    } catch (e) { /* ignore */ }

    fetchAllPages(1, []);
  }

  function fetchAllPages(page, accumulated) {
    fetch('/products.json?limit=' + PRODUCTS_PER_PAGE + '&page=' + page)
      .then(function (res) {
        if (!res.ok) throw new Error(res.status);
        return res.json();
      })
      .then(function (data) {
        var fetched = data.products || [];
        var all = accumulated.concat(fetched);

        if (fetched.length === PRODUCTS_PER_PAGE) {
          fetchAllPages(page + 1, all);
        } else {
          products = all.map(extractProduct);
          try {
            sessionStorage.setItem(CACHE_KEY, JSON.stringify(products));
          } catch (e) { /* quota exceeded */ }
          buildIndex();
          isLoading = false;
        }
      })
      .catch(function () {
        isLoading = false;
      });
  }

  function extractProduct(p) {
    var img = null;
    if (p.images && p.images.length > 0) {
      img = p.images[0].src;
    }
    var price = 0;
    var compareAtPrice = null;
    if (p.variants && p.variants.length > 0) {
      // Find lowest price
      var minPrice = Infinity;
      var minCompare = null;
      for (var i = 0; i < p.variants.length; i++) {
        var v = p.variants[i];
        var vp = parseFloat(v.price);
        if (vp < minPrice) {
          minPrice = vp;
          minCompare = v.compare_at_price ? parseFloat(v.compare_at_price) : null;
        }
      }
      price = minPrice === Infinity ? 0 : minPrice;
      compareAtPrice = minCompare;
    }
    return {
      handle: p.handle,
      title: p.title || '',
      description: stripHtml(p.body_html || ''),
      tags: (p.tags || []).join(' '),
      product_type: p.product_type || '',
      vendor: p.vendor || '',
      url: '/products/' + p.handle,
      image: img,
      price: price,
      compareAtPrice: compareAtPrice,
      priceVaries: p.variants && p.variants.length > 1 &&
        new Set(p.variants.map(function (v) { return v.price; })).size > 1,
      available: p.variants ? p.variants.some(function (v) { return v.available; }) : true
    };
  }

  // ── Trigram Helpers ───────────────────────────────────────────────

  function getTrigrams(word) {
    if (word.length < 3) return [word];
    var trigrams = [];
    for (var i = 0; i <= word.length - 3; i++) {
      trigrams.push(word.slice(i, i + 3));
    }
    return trigrams;
  }

  function jaccardSimilarity(setA, setB) {
    var intersection = 0;
    for (var i = 0; i < setA.length; i++) {
      if (setB.indexOf(setA[i]) !== -1) intersection++;
    }
    var union = setA.length + setB.length - intersection;
    return union === 0 ? 0 : intersection / union;
  }

  function buildTrigramIndex(vocab) {
    trigramIndex = {};
    vocab.forEach(function (term) {
      var trigrams = getTrigrams(term);
      for (var i = 0; i < trigrams.length; i++) {
        var tri = trigrams[i];
        if (!trigramIndex[tri]) trigramIndex[tri] = [];
        trigramIndex[tri].push(term);
      }
    });
  }

  function findFuzzyMatch(token, threshold) {
    if (!trigramIndex) return null;
    threshold = threshold || 0.3;
    var queryTrigrams = getTrigrams(token);
    // Collect candidate terms from trigram index
    var candidates = {};
    for (var i = 0; i < queryTrigrams.length; i++) {
      var terms = trigramIndex[queryTrigrams[i]];
      if (!terms) continue;
      for (var j = 0; j < terms.length; j++) {
        candidates[terms[j]] = true;
      }
    }
    var bestMatch = null;
    var bestScore = threshold;
    var candidateKeys = Object.keys(candidates);
    for (var k = 0; k < candidateKeys.length; k++) {
      var candidate = candidateKeys[k];
      var candidateTrigrams = getTrigrams(candidate);
      var score = jaccardSimilarity(queryTrigrams, candidateTrigrams);
      if (score > bestScore) {
        bestScore = score;
        bestMatch = candidate;
      }
    }
    return bestMatch;
  }

  // ── Index Builder ──────────────────────────────────────────────────

  function buildIndex() {
    invertedIndex = {};
    var dfMap = {};
    docCount = products.length;

    // First pass: build vocabulary from all product tokens for decompounding
    vocabulary = new Set();
    for (var p = 0; p < products.length; p++) {
      var prod = products[p];
      var fields = ['title', 'tags', 'product_type', 'vendor'];
      for (var f = 0; f < fields.length; f++) {
        var tokens = tokenize(prod[fields[f]]);
        for (var t = 0; t < tokens.length; t++) {
          vocabulary.add(tokens[t]);
        }
      }
    }

    // Second pass: build inverted index with TF-IDF
    for (var i = 0; i < products.length; i++) {
      var product = products[i];
      var docTokenSets = {};

      var fieldNames = ['title', 'tags', 'product_type', 'vendor', 'description'];
      for (var fi = 0; fi < fieldNames.length; fi++) {
        var fieldName = fieldNames[fi];
        var fieldTokens = tokenize(product[fieldName]);

        // Decompound tokens using vocabulary
        var expanded = [];
        for (var ti = 0; ti < fieldTokens.length; ti++) {
          var parts = decompoundWord(fieldTokens[ti], vocabulary);
          for (var pi = 0; pi < parts.length; pi++) {
            expanded.push(parts[pi]);
          }
        }

        // Count term frequency per field
        var tf = {};
        for (var ei = 0; ei < expanded.length; ei++) {
          var token = expanded[ei];
          tf[token] = (tf[token] || 0) + 1;

          if (!docTokenSets[token]) {
            docTokenSets[token] = true;
          }
        }

        // Add to inverted index
        var tfKeys = Object.keys(tf);
        for (var ki = 0; ki < tfKeys.length; ki++) {
          var tk = tfKeys[ki];
          if (!invertedIndex[tk]) invertedIndex[tk] = [];
          invertedIndex[tk].push({
            idx: i,
            field: fieldName,
            tf: tf[tk]
          });
        }
      }

      // Update document frequency
      var docTokenKeys = Object.keys(docTokenSets);
      for (var di = 0; di < docTokenKeys.length; di++) {
        var dtk = docTokenKeys[di];
        dfMap[dtk] = (dfMap[dtk] || 0) + 1;
      }
    }

    // Compute IDF
    idfMap = {};
    var dfKeys = Object.keys(dfMap);
    for (var ii = 0; ii < dfKeys.length; ii++) {
      var key = dfKeys[ii];
      idfMap[key] = Math.log(1 + docCount / (1 + dfMap[key]));
    }

    // Build trigram index for fuzzy matching
    buildTrigramIndex(vocabulary);

    isReady = true;
  }

  // ── Search ─────────────────────────────────────────────────────────

  function search(query, excludeHandles, limit) {
    if (!isReady || !products || !invertedIndex) {
      return { html: '', count: 0 };
    }

    limit = limit || MAX_RESULTS;
    var excludeSet = new Set(excludeHandles || []);

    // Tokenize query
    var queryTokens = tokenize(query);
    if (queryTokens.length === 0) return { html: '', count: 0 };

    // Decompound query tokens
    var expandedQuery = [];
    for (var q = 0; q < queryTokens.length; q++) {
      var parts = decompoundWord(queryTokens[q], vocabulary || new Set());
      for (var p = 0; p < parts.length; p++) {
        expandedQuery.push(parts[p]);
      }
    }

    // Add synonyms
    var allTokens = expandedQuery.slice();
    for (var s = 0; s < expandedQuery.length; s++) {
      var syns = SYNONYMS[expandedQuery[s]];
      if (syns) {
        for (var si = 0; si < syns.length; si++) {
          allTokens.push(syns[si]);
        }
      }
    }

    // Score products
    var scores = {};
    for (var ti = 0; ti < allTokens.length; ti++) {
      var token = allTokens[ti];
      var idf = idfMap[token];

      // Try prefix matching, then trigram fuzzy matching for unknown tokens
      if (!idf) {
        var matched = false;
        // First: prefix matching (fast, high confidence)
        var indexKeys = Object.keys(invertedIndex);
        for (var ik = 0; ik < indexKeys.length; ik++) {
          var indexKey = indexKeys[ik];
          if (
            (token.length >= 3 && indexKey.indexOf(token) === 0) ||
            (indexKey.length >= 3 && token.indexOf(indexKey) === 0)
          ) {
            matched = true;
            idf = idfMap[indexKey];
            var postings = invertedIndex[indexKey];
            if (postings) {
              for (var pi = 0; pi < postings.length; pi++) {
                var posting = postings[pi];
                var product = products[posting.idx];
                if (excludeSet.has(product.handle)) continue;

                var weight = FIELD_WEIGHTS[posting.field] || 1.0;
                var score = posting.tf * (idf || 1) * weight * 0.6;
                scores[posting.idx] = (scores[posting.idx] || 0) + score;
              }
            }
          }
        }
        // Second: trigram fuzzy matching (handles typos)
        if (!matched && token.length >= 4) {
          var fuzzyMatch = findFuzzyMatch(token, 0.3);
          if (fuzzyMatch && invertedIndex[fuzzyMatch]) {
            idf = idfMap[fuzzyMatch];
            var fPostings = invertedIndex[fuzzyMatch];
            for (var fi = 0; fi < fPostings.length; fi++) {
              var fPosting = fPostings[fi];
              var fProduct = products[fPosting.idx];
              if (excludeSet.has(fProduct.handle)) continue;

              var fWeight = FIELD_WEIGHTS[fPosting.field] || 1.0;
              var fScore = fPosting.tf * (idf || 1) * fWeight * 0.5;
              scores[fPosting.idx] = (scores[fPosting.idx] || 0) + fScore;
            }
          }
        }
        continue;
      }

      var postings = invertedIndex[token];
      if (!postings) continue;

      // Apply a discount for synonym-only matches (not in the original query)
      var isSynonym = ti >= expandedQuery.length;
      var synDiscount = isSynonym ? 0.5 : 1.0;

      for (var pi = 0; pi < postings.length; pi++) {
        var posting = postings[pi];
        var product = products[posting.idx];
        if (excludeSet.has(product.handle)) continue;

        var weight = FIELD_WEIGHTS[posting.field] || 1.0;
        var score = posting.tf * idf * weight * synDiscount;
        scores[posting.idx] = (scores[posting.idx] || 0) + score;
      }
    }

    // Sort by score
    var scored = Object.keys(scores).map(function (idx) {
      return { idx: parseInt(idx, 10), score: scores[idx] };
    });
    scored.sort(function (a, b) { return b.score - a.score; });

    // Limit results
    var results = scored.slice(0, limit);
    if (results.length === 0) return { html: '', count: 0 };

    // Build HTML
    var html = buildResultsHtml(results);
    return { html: html, count: results.length };
  }

  // ── HTML Builder ───────────────────────────────────────────────────

  function formatMoney(cents) {
    var format = '';
    try {
      format = window.theme.settings.moneyWithCurrencyFormat || '{{amount}} EUR';
    } catch (e) {
      format = '{{amount}} EUR';
    }

    var amount = (cents / 100).toFixed(2);
    // Use comma as decimal separator for German formatting
    var amountWithComma = amount.replace('.', ',');

    // Handle different Shopify money format placeholders
    return format
      .replace('{{amount_with_comma_separator}}', amountWithComma)
      .replace('{{ amount_with_comma_separator }}', amountWithComma)
      .replace('{{amount_no_decimals_with_comma_separator}}', amountWithComma.split(',')[0])
      .replace('{{ amount_no_decimals_with_comma_separator }}', amountWithComma.split(',')[0])
      .replace('{{amount_with_apostrophe_separator}}', amount)
      .replace('{{ amount_with_apostrophe_separator }}', amount)
      .replace('{{amount_no_decimals}}', Math.round(cents / 100).toString())
      .replace('{{ amount_no_decimals }}', Math.round(cents / 100).toString())
      .replace('{{amount}}', amount)
      .replace('{{ amount }}', amount);
  }

  function escapeHtml(str) {
    if (!str) return '';
    return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  function buildResultsHtml(results) {
    var html = '<div class="semantic-search-results">';
    html += '<h3 class="predictive-result__title h6 tablist__scroller font-body regular-text font-bold mt-3">Weitere Vorschl\u00e4ge</h3>';
    html += '<ul class="predictive-search__list" role="list">';

    for (var i = 0; i < results.length; i++) {
      var product = products[results[i].idx];
      var id = 'semantic-result-' + i;

      html += '<li class="predictive-search__item" id="' + id + '" role="option">';
      html += '<a class="predictive-result flex items-start focus-inset js-search-link" href="' + escapeHtml(product.url) + '">';

      // Image
      if (product.image) {
        var imgSrc = product.image;
        // Resize via Shopify CDN if possible
        if (imgSrc.indexOf('.jpg') > -1 || imgSrc.indexOf('.png') > -1 || imgSrc.indexOf('.webp') > -1) {
          imgSrc = imgSrc.replace(/(\.(jpg|png|webp))/, '_112x$1');
        }
        html += '<div class="predictive-result__media media relative">';
        html += '<img src="' + escapeHtml(imgSrc) + '" alt="' + escapeHtml(product.title) + '" loading="eager" class="predictive-result__media" width="112" height="112">';
        html += '</div>';
      } else {
        html += '<div class="predictive-result__media media relative">';
        html += '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 525.5 525.5" class="media__placeholder"><path d="M375.5 345.2c0-.1 0-.1 0 0 0-.1 0-.1 0 0-1.1-2.9-2.3-5.5-3.4-7.8-1.7-4.2-3.4-7.4-4.4-8.7-.1-.1-.1-.2-.2-.3-3-3.6-7.7-5.8-12.5-6-5-.1-10.4 1.6-15 4.5-2.4 1.5-4.6 3.3-6.3 5.3-3.1 3.5-5 7.6-5.2 12v.1c0 .1 0 .2-.1.4v.2c0 .1 0 .2-.1.3v.3c0 .1 0 .2-.1.3v.5c0 .1 0 .2-.1.3v.5c0 .1 0 .1-.1.2v.4l-.1.3c0 .1 0 .1-.1.2v.3c-.1.1-.1.2-.1.3-.1.3-.1.5-.2.7l-.3.6-.3.6-.3.6-.3.6-.2.4-.2.4c-.2.4-.4.7-.6 1-.7 1-1.5 1.7-2.2 2.3-.7.5-1.4.9-2 1.2-.2.1-.3.1-.4.2s-.2.1-.3.1-.2.1-.3.1-.1.1-.2.1h-.1c-.6.3-1 .4-1.3.5h-.2c-.1 0-.2.1-.3.1-1.5.2-3 .3-4.4.3-1 0-2-.1-3-.1-.7-.1-1.3-.1-1.9-.2-.3 0-.6-.1-.8-.1-.2-.1-.4-.1-.6-.1-.2 0-.4-.1-.5-.1-.1 0-.2 0-.3-.1l-.1-.1h-.1l.1.1c-.3 0-.5-.1-.7-.2-.4-.1-.8-.2-1.1-.3-.2-.1-.4-.1-.6-.2-.5-.2-1-.5-1.4-.8-.2-.1-.3-.2-.4-.3-.1-.1-.2-.1-.3-.2-.2-.2-.4-.3-.5-.5-.6-.6-1.1-1.2-1.4-2-.4-.8-.7-1.7-.8-2.7v-.1c-.1-.4-.1-.8-.1-1.2v-.3-.2-.1-.1c0-.1 0-.2.1-.4v-.2c0-.1 0-.2.1-.3l.1-.1v-.3c0-.1.1-.2.1-.3l.1-.1c.1-.1.1-.2.1-.3l.1-.1c.1-.1.1-.2.1-.3.1-.1.1-.2.1-.3l.1-.1c.1-.1.1-.2.2-.3.1-.1.1-.2.2-.3.2-.3.4-.7.6-1 .1-.2.3-.4.4-.5.8-1.1 1.9-1.9 3-2.2 1-.3 2.1-.3 3.2 0 .2.1.5.1.7.2.4.2.8.3 1.2.5.5.3 1 .7 1.4 1.2.8.7 1.4 1.6 2 2.6.2.3.3.6.5.9.3.6.5 1.2.7 1.8l.2.6c.3 1 .4 2 .5 3v.6c0 .3 0 .5-.1.8 0 .1 0 .2-.1.3 0 .1 0 .2-.1.3-.1.2-.1.4-.2.6 0 .1-.1.2-.1.3l-.3.6c0 .1-.1.2-.1.3-.2.3-.4.7-.7 1-.3.4-.6.7-.9 1-.3.3-.6.5-1 .7l-.6.3c-.2.1-.3.1-.5.2-.2.1-.3.1-.5.1-.2.1-.4.1-.5.1h-.4c-.2 0-.3 0-.5-.1-.1 0-.2 0-.3-.1-.1 0-.2 0-.3-.1l-.3-.1h-.1l-.2-.1c-.6-.3-1-.7-1.4-1-.8-.7-1.2-1.6-1.4-2.5-.1-.3-.1-.6-.1-1v-.8c0-.3.1-.6.2-.9.1-.3.2-.6.4-.9.1-.3.3-.5.5-.7.2-.3.5-.5.7-.7 0 0 .1-.1.2-.1l.3-.3c.3-.2.5-.3.8-.4h.1l.5-.2h.2c.2 0 .3-.1.5-.1h.6c.1 0 .3 0 .4.1h.1c.2.1.4.1.5.2.2.1.3.2.5.3.5.4.9.8 1.2 1.4l.3.6c.1.2.1.5.2.7v.3c0 .2 0 .3-.1.5 0 .1 0 .2-.1.3-.1.2-.2.4-.3.5-.1.2-.3.3-.5.4l-.4.3c-.1.1-.3.1-.4.2h-.2c-.1 0-.2 0-.3.1h-.5l-.3-.1c-.2-.1-.3-.2-.5-.3-.2-.1-.3-.3-.4-.5 0-.1-.1-.1-.1-.2 0-.1-.1-.2-.1-.3v-.5c0-.2 0-.3.1-.5.1-.2.2-.3.3-.4.1-.1.3-.2.5-.2h.1l.3-.1h.3c.1 0 .2 0 .3.1h.1c.1 0 .2.1.3.2.3.2.4.5.5.8v.4c0 .1 0 .3-.1.4-.1.1-.2.3-.3.3-.2.1-.3.1-.5.1H349.2c-.1 0-.2 0-.3-.1-.1-.1-.2-.2-.2-.3v-.4c.1-.2.2-.4.4-.4h.5c.1 0 .2 0 .2-.1.1-.1.1-.2.1-.3-.1-.1-.2-.2-.3-.2h-.6c-.2.1-.4.2-.5.4-.1.2-.2.4-.2.6v.2c0 .1 0 .2.1.3l.1.1c.1.1.1.2.2.3l.2.2c.1 0 .1.1.2.1.1.1.2.1.3.1.1.1.2.1.3.1h.7c.2 0 .3-.1.5-.1.1-.1.2-.1.3-.2l.3-.3c.1-.1.2-.3.2-.5.1-.2.1-.4.1-.6v-.1c0-.3-.1-.5-.2-.7-.1-.3-.3-.5-.5-.6-.2-.2-.4-.3-.6-.3-.1-.1-.3-.1-.5-.1h-.5c-.1 0-.3 0-.4.1-.1 0-.2.1-.3.1-.1.1-.2.1-.3.2-.1.1-.2.2-.2.3-.1.1-.2.3-.2.4-.1.2-.1.3-.1.5v.4c0 .2.1.4.1.5.1.2.2.3.3.5.1.1.3.3.5.4.2.1.4.2.6.3l.3.1h.6l.4-.1c.2 0 .3-.1.5-.2l.4-.3c.2-.1.3-.3.4-.5.1-.2.2-.4.3-.6.1-.3.1-.5.1-.8v-.3c0-.3-.1-.5-.1-.8-.1-.3-.2-.5-.3-.7-.2-.3-.4-.5-.6-.7-.3-.2-.5-.4-.8-.5-.3-.1-.6-.2-.9-.2h-.7c-.2 0-.4.1-.6.1-.2.1-.4.2-.6.3-.2.1-.3.3-.5.4-.3.3-.5.6-.7 1-.2.4-.3.8-.3 1.2v.6c0 .3 0 .6.1.9 0 .3.1.6.2.8.2.5.4.9.7 1.3.3.4.7.7 1.1.9.5.3 1 .4 1.5.5h.8c.3 0 .6-.1.9-.1.3-.1.6-.2.9-.4l.6-.3c.1-.1.3-.2.4-.4.2-.2.3-.4.5-.6.1-.2.3-.4.4-.7.1-.3.2-.6.2-.9.1-.3.1-.6.1-.9v-.5c-.1-.5-.2-1-.3-1.5-.2-.5-.4-.9-.7-1.3-.3-.4-.7-.8-1.1-1.1-.4-.3-.9-.5-1.3-.7-.3-.1-.5-.1-.8-.2-.4-.1-.8-.1-1.2-.1-.3 0-.7.1-1 .1-.3.1-.7.2-1 .3-.6.3-1.2.7-1.7 1.1-.5.5-.9 1-1.2 1.6-.1.2-.2.5-.3.7-.1.3-.2.6-.2.9-.1.3-.1.6-.1.9v.8c.1.4.1.7.2 1.1.1.4.3.8.5 1.1.3.5.6 1 1 1.4.4.4.9.7 1.4 1 .5.3 1.1.4 1.7.5.3.1.7.1 1 .1.4 0 .7 0 1.1-.1l.9-.3c.6-.2 1.1-.6 1.6-1 .5-.4.9-.9 1.2-1.5l.4-.8c.1-.3.2-.6.3-.9.1-.4.1-.7.2-1.1V345.2z" fill="currentColor"/></svg>';
        html += '</div>';
      }

      // Info
      html += '<div class="predictive-result__info flex-auto">';
      html += '<h3 class="predictive-result__title h6">' + escapeHtml(product.title) + '</h3>';

      // Price
      if (product.price > 0) {
        var priceInCents = Math.round(product.price * 100);
        html += '<div class="price';
        if (product.compareAtPrice && product.compareAtPrice > product.price) {
          html += ' price--on-sale';
        }
        if (!product.available) {
          html += ' price--sold-out';
        }
        html += '">';
        html += '<div class="price__default">';
        html += '<span class="price__current">';
        if (product.priceVaries) {
          html += '<span class="price__from">ab </span>';
        }
        html += '<span class="js-value">' + formatMoney(priceInCents) + '</span>';
        html += '</span>';
        if (product.compareAtPrice && product.compareAtPrice > product.price) {
          var compareCents = Math.round(product.compareAtPrice * 100);
          html += '<span class="price__was">';
          html += '<span class="js-value">' + formatMoney(compareCents) + '</span>';
          html += '</span>';
        }
        html += '</div></div>';
      }

      // Vendor
      if (product.vendor) {
        html += '<div class="predictive-result__sub-title text-sm">' + escapeHtml(product.vendor) + '</div>';
      }

      html += '</div></a></li>';
    }

    html += '</ul></div>';
    return html;
  }

  // ── Public API ─────────────────────────────────────────────────────

  window.SemanticSearch = {
    init: function () {
      fetchProducts();
    },
    search: function (query, excludeHandles, limit) {
      return search(query, excludeHandles, limit);
    },
    isReady: function () {
      return isReady;
    }
  };

  // ── Auto-init on page load ─────────────────────────────────────────
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      window.SemanticSearch.init();
    });
  } else {
    window.SemanticSearch.init();
  }
})();
